import React from 'react';
import { Variant, DataScenario } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectLevel1Data, selectLevel2Data } from 'store/dataReducers/featureMetricsSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading, selectDataScenariosError } from 'store/dataReducers/dataScenariosSlice';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Menu,
  ListItemText,
  Divider,
  alpha
} from '@mui/material';
import {
  Save,
  GitCompareArrows,
  FileClock,
  ClipboardCheck,
  Monitor,
  Smartphone,
  Network,
  Languages,
  Layers,
  Library,
  ChevronDown,
  PlusCircle,
  CheckCheck
} from 'lucide-react';

// Default locales - can be replaced with API data later
const DEFAULT_LOCALES = ['en_US', 'es_US'];

// EditorToolbar no longer makes API calls - moved to WysiwygPage to prevent duplicates

type Props = {
  templateKey: string;
  elementKey: string;
  variant: Variant;
  channel?: string;
  previewMode?: 'desktop' | 'mobile';
  onElementChange: (k: string) => void;
  onVariantChange: (v: Variant) => void;
  onPreviewModeChange?: (mode: 'desktop' | 'mobile') => void;
  onSave?: () => Promise<void> | void;
  onOpenCompare?: () => void;
  onCancel?: () => void;
  renderOnlyUpperSection?: boolean;
  renderOnlyLowerSection?: boolean;
  availableElements?: Array<{ elementKey: string; displayName: string }>; // Elements parsed from rendered HTML (optional)
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  modifiedCount?: number; // Number of modified fields (for Save button counter)
  signedOffElements?: Set<string>; // Set of element keys that are signed off (for CheckCheck icon display)
}

export const EditorToolbar: React.FC<Props> = ({ 
  templateKey, 
  elementKey, 
  variant, 
  channel = 'Email',
  previewMode = 'desktop',
  onElementChange, 
  onVariantChange,
  onPreviewModeChange,
  onSave, 
  onOpenCompare,
  onCancel,
  renderOnlyUpperSection = false,
  renderOnlyLowerSection = false,
  availableElements,
  onRenderComplete,
  modifiedCount = 0,
  signedOffElements = new Set<string>()
}) => {
  const { rc } = useGlobalConfig();
  const level2Data = useSelector(selectLevel2Data);
  const [elements, setElements] = React.useState<{ elementKey: string; displayName: string }[]>([]);
  
  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  const locale = variant.locale || 'en_US';

  // Get error state from Redux
  const scenariosError = useSelector((state: RootState) =>
    selectedPilotId
      ? selectDataScenariosError(state, String(selectedPilotId), fuelType, locale)
      : null
  );

  // Format locale for display (e.g., "en_US" -> "English (US)")
  const localeDisplayText = React.useMemo(() => {
    const localeMap: Record<string, string> = {
      'en_US': 'English (US)',
      'es_US': 'Spanish (US)',
      'en_CA': 'English (CA)',
      'fr_CA': 'French (CA)',
    };
    return localeMap[locale] || locale;
  }, [locale]);

  // Get scenarios from Redux (full DataScenario objects)
  const allScenarios = useSelector((state: RootState) => 
    templateKey && selectedPilotId
      ? selectScenariosForTemplate(
          state,
          String(selectedPilotId),
          fuelType,
          locale,
          templateKey,
          channel
        )
      : []
  );

  // Helper function to get technical section name from elementKey
  // Returns "FULL_TEMPLATE" for "Full Template", or technical section ID (e.g., "MONTHLY_SUMMARY_V2")
  const getTechnicalSectionName = React.useCallback((elementKey: string): string => {
    if (elementKey === 'Full Template') {
      return 'FULL_TEMPLATE';
    }
    
    // Check if elementKey is already a technical section ID (exists as key in mapping)
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return elementKey;
    }
    
    // Reverse lookup: Find all technical section IDs that map to this display name
    // Prefer higher version numbers (e.g., V4 > V3 > V2)
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === elementKey) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      // Sort by version number (higher first) and return the first one
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; // Higher version first
      });
      return matchingTechnicalIds[0];
    }
    
    // Fallback: if elementKey might be a technical section ID that's not in mapping, return as-is
    return elementKey;
  }, []);

  // Helper function to extract base template name from templateKey for matching with elementNames
  // Example: "EMAIL.SF.MONTHLY_SUMMARY" -> "MONTHLY_SUMMARY"
  // This helps match templateKey with elementNames like "MONTHLY_SUMMARY_V2"
  const getBaseTemplateName = React.useCallback((templateKey: string): string => {
    if (!templateKey) return '';
    // Split by dots and take the last part (e.g., "EMAIL.SF.MONTHLY_SUMMARY" -> "MONTHLY_SUMMARY")
    const parts = templateKey.split('.');
    return parts[parts.length - 1] || templateKey;
  }, []);

  // Filter scenarios by templateName and elementNames
  // More lenient filtering: handles empty elementNames, Full Template case, and rendering failures
  // Also ensures template name from Template Sidebar is present in elementNames
  const scenarios = React.useMemo(() => {
    if (!templateKey) {
      return allScenarios;
    }

    // Get base template name for matching with elementNames
    const baseTemplateName = getBaseTemplateName(templateKey);

    // If elementKey is not available (e.g., rendering failed), show scenarios for the template
    // but still check that template name is in elementNames
    if (!elementKey) {
      return allScenarios.filter((scenario: DataScenario) => {
        // Check templateName match
        if (scenario.templateName !== templateKey) {
          return false;
        }
        
        // Check that template name is present in elementNames
        // Require elementNames to be non-empty and contain the template name
        const elementNames = scenario.elementNames || [];
        if (elementNames.length === 0) {
          return false; // Don't show scenarios with empty elementNames
        }
        
        // Check if base template name appears in any elementName (e.g., "MONTHLY_SUMMARY" in "MONTHLY_SUMMARY_V2")
        return elementNames.some(name => 
          name === baseTemplateName || 
          name.startsWith(baseTemplateName + '_') ||
          name.includes(baseTemplateName)
        );
      });
    }

    // Get technical section name for current elementKey
    const technicalSectionName = getTechnicalSectionName(elementKey);

    // Filter scenarios with lenient matching:
    // 1. templateName must match templateKey
    // 2. elementNames array must include the technical section name OR be empty (empty means applies to all elements)
    // 3. template name from Template Sidebar should be present in elementNames
    const filtered = allScenarios.filter((scenario: DataScenario) => {
      // Check templateName match
      const templateNameMatch = scenario.templateName === templateKey;
      if (!templateNameMatch) {
        return false;
      }
      
      // Check elementNames array
      const elementNames = scenario.elementNames || [];
      
      // Require elementNames to be non-empty - don't show scenarios with empty elementNames
      // The template name from Template Sidebar must be present in elementNames
      if (elementNames.length === 0) {
        return false;
      }
      
      // Check if elementNames includes the technical section name
      const hasSection = elementNames.includes(technicalSectionName);
      
      // Special case: if elementKey is "Full Template", also check for "FULL_TEMPLATE" in elementNames
      if (elementKey === 'Full Template' && !hasSection) {
        return elementNames.includes('FULL_TEMPLATE');
      }
      
      // If section is not found, don't show this scenario
      if (!hasSection) {
        return false;
      }
      
      // Additionally, check that template name from Template Sidebar is present in elementNames
      // This ensures scenarios are properly filtered by the selected template
      // The template name check is lenient - it matches if the base template name appears in any elementName
      const hasTemplateName = elementNames.some(name => {
        // Exact match (e.g., "MONTHLY_SUMMARY" === "MONTHLY_SUMMARY")
        if (name === baseTemplateName) return true;
        // Match with version suffix (e.g., "MONTHLY_SUMMARY_V2" starts with "MONTHLY_SUMMARY_")
        if (name.startsWith(baseTemplateName + '_')) return true;
        // Match if base template name is contained in the element name (lenient fallback)
        if (name.includes(baseTemplateName)) return true;
        return false;
      });
      
      // Show scenario if it has the section AND the template name is present in elementNames
      // This ensures proper filtering: both the element section and the template must match
      return hasSection && hasTemplateName;
    });

    // If filtering resulted in empty array, fall back to showing scenarios for the template
    // that have non-empty elementNames and contain the template name
    // This handles cases where elementNames might not be properly set in the API response
    if (filtered.length === 0 && allScenarios.length > 0) {
      console.warn('[EditorToolbar] No scenarios matched element filter, falling back to template-only filter:', {
        templateKey,
        elementKey,
        technicalSectionName,
        baseTemplateName,
        allScenariosCount: allScenarios.length
      });
      return allScenarios.filter((scenario: DataScenario) => {
        // Check templateName match
        if (scenario.templateName !== templateKey) {
          return false;
        }
        
        // Still require elementNames to be non-empty and contain template name
        const elementNames = scenario.elementNames || [];
        if (elementNames.length === 0) {
          return false; // Don't show scenarios with empty elementNames
        }
        
        // Check if base template name appears in any elementName
        return elementNames.some(name => 
          name === baseTemplateName || 
          name.startsWith(baseTemplateName + '_') ||
          name.includes(baseTemplateName)
        );
      });
    }

    return filtered;
  }, [allScenarios, templateKey, elementKey, getTechnicalSectionName, getBaseTemplateName]);

  // Get loading state from Redux
  const scenariosLoading = useSelector((state: RootState) =>
    selectedPilotId
      ? selectDataScenariosLoading(state, String(selectedPilotId), fuelType, locale)
      : false
  );

  const [saving, setSaving] = React.useState(false);
  const [clusters, setClusters] = React.useState<string[]>([]);
  const [locales, setLocales] = React.useState<string[]>(DEFAULT_LOCALES);
  const [hierarchyMenuOpen, setHierarchyMenuOpen] = React.useState(false);
  const [hierarchyAnchorEl, setHierarchyAnchorEl] = React.useState<null | HTMLElement>(null);
  const [clusterMenuOpen, setClusterMenuOpen] = React.useState(false);

  // Transform Level 2 data to elements for the dropdown
  // Filter by productType (templateKey) and resolution === "ACCESS"
  const level2Elements = React.useMemo(() => {
    if (!level2Data || !templateKey) {
      return [];
    }

    // Filter Level 2 data by productType matching templateKey and resolution === "ACCESS"
    const filtered = level2Data
      .filter(resolution => 
        resolution.productType === templateKey && 
        resolution.resolution === 'ACCESS' &&
        resolution.section // Only include items with a section
      )
      .sort((a, b) => {
        // Sort by position if available, otherwise by section name
        if (a.position !== null && b.position !== null) {
          return a.position - b.position;
        }
        if (a.position !== null) return -1;
        if (b.position !== null) return 1;
        return (a.section || '').localeCompare(b.section || '');
      })
      .map(resolution => {
        // Convert section name to display name using comprehensive mapping
        // Example: "HEADER_V4" -> "Header" (from mapping) or "Header V4" (fallback)
        const sectionName = resolution.section || '';
        
        // Try to get display name from mapping first
        let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionName];
        
        // If no exact match, try removing version suffix (e.g., _V2, _V3, _V4)
        if (!displayName) {
          const baseId = sectionName.replace(/_[Vv]\d+$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
        }
        
        // If still no match, try removing all version suffixes
        if (!displayName) {
          const cleanId = sectionName.replace(/_[Vv]\d+.*$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
        }
        
        // Fallback to simple transformation if no mapping found
        if (!displayName) {
          displayName = sectionName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
        
        return {
          elementKey: sectionName,
          displayName: displayName
        };
      });

    return filtered;
  }, [level2Data, templateKey]);

  // Update elements: prefer Level 2 data, fallback to availableElements from props
  React.useEffect(() => {
    if (level2Elements.length > 0) {
      // Use Level 2 data with "Full Template" as first option
      setElements([
        { elementKey: 'Full Template', displayName: 'Full Template' },
        ...level2Elements
      ]);
    } else if (availableElements && availableElements.length > 0) {
      // Fallback to availableElements from props (parsed from HTML)
      setElements(availableElements);
    } else {
      // Default fallback
      setElements([{ elementKey: 'Full Template', displayName: 'Full Template' }]);
    }
  }, [level2Elements, availableElements]);
  
  // EditorToolbar no longer fetches template meta - it's done in WysiwygPage to prevent duplicates
  // Scenarios are passed via props/callback from WysiwygPage

  React.useEffect(() => {
    let ignore = false;
    async function loadClusters() {
      try {
        if (!selectedPilotId) return;
        const resp = await rc.bffClient.getClusters(String(selectedPilotId));
        const list = (resp as any)?.data?.clusters || (resp as any)?.data || [];
        const names: string[] = Array.isArray(list) ? list.map((c: any) => c.name || c.cluster_name || c.clusterName || String(c)) : [];
        if (!ignore) setClusters(names);
      } catch {
        if (!ignore) setClusters([]);
      }
      
      // TODO: Phase 2 - Uncomment when ready to use real API
      // try {
      //   if (!selectedPilotId) return;
      //   const resp = await ClusterApis.getClusters(rc, String(selectedPilotId));
      //   if (resp.success && resp.data) {
      //     const list = (resp.data as any)?.clusters || (resp.data as any)?.data || resp.data || [];
      //     const names: string[] = Array.isArray(list) ? list.map((c: any) => c.name || c.cluster_name || c.clusterName || String(c)) : [];
      //     if (!ignore) setClusters(names);
      //   }
      // } catch {
      //   if (!ignore) setClusters([]);
      // }
    }
    loadClusters();
    return () => { ignore = true; };
  }, [selectedPilotId, rc]);

  // Load supported locales from API
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      if (!selectedPilotId) {
        if (!ignore) {
          setLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            onVariantChange({ ...variant, locale: DEFAULT_LOCALES[0] });
          }
        }
        return;
      }
      try {
        const resp = await rc.bffClient.getProjectLocales(String(selectedPilotId));
        const list = (resp as any)?.data?.locales || DEFAULT_LOCALES;
        if (!ignore) {
          setLocales(list);
          if (!list.includes(variant.locale)) {
            onVariantChange({ ...variant, locale: list[0] });
          }
        }
      } catch {
        if (!ignore) {
          setLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            onVariantChange({ ...variant, locale: DEFAULT_LOCALES[0] });
          }
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedPilotId, rc, variant.locale, onVariantChange]);

  // Get template name and channel from Redux Level 1 data
  const level1Data = useSelector(selectLevel1Data);
  const { templateName, templateChannel } = React.useMemo(() => {
    if (!templateKey) return { templateName: '', templateChannel: channel };
    
    // Try to find template in Level 1 data
    if (level1Data) {
      const templateResolution = level1Data.find(r => r.featureId === templateKey);
      if (templateResolution) {
        // Extract display name from featureId (e.g., "EMAIL.SF.MONTHLY_SUMMARY" -> "Monthly Summary")
        const parts = templateResolution.featureId.split('.');
        const lastPart = parts[parts.length - 1] || templateResolution.featureId;
        const displayName = lastPart
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        // Map product to channel
        const product = templateResolution.product.toUpperCase();
        let channelName = channel;
        if (product === 'EMAIL') channelName = 'Email';
        else if (product === 'HER') channelName = 'HER';
        else if (product === 'WEB') channelName = 'Web';
        else if (product === 'SMS') channelName = 'SMS';
        
        return { templateName: displayName, templateChannel: channelName };
      }
    }
    
    // Fallback: use templateKey as name
    return { templateName: templateKey, templateChannel: channel };
  }, [templateKey, channel, level1Data]);

  // Extract prefix from template name, or default to EL for Email templates
  const prefixMatch = templateName.match(/\(([^)]+)\)/);
  let prefix = prefixMatch ? prefixMatch[0] : '';
  // If no prefix in template name but it's an Email template, default to EL
  if (!prefix && templateChannel === 'Email') {
    prefix = '(EL)';
  }
  const nameWithoutPrefix = templateName.replace(/\(([^)]+)\)/, '').trim();
  const displayPrefix = prefix.replace(/[()]/g, '');

  const hierarchyValue = variant.hierarchy.startsWith('Cluster:') 
    ? variant.hierarchy.replace('Cluster:', '') 
    : variant.hierarchy;
  const hierarchyDisplayText = hierarchyValue === 'Project' ? 'Project' : `Cluster: ${hierarchyValue}`;
  const selectedScenarioLabel = scenarios.find(ds => ds.scenarioId === variant.scenario)?.scenarioDisplayName || variant.scenario;

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleHierarchyClick = (event: React.MouseEvent<HTMLElement>) => {
    setHierarchyAnchorEl(event.currentTarget);
    setHierarchyMenuOpen(true);
  };

  const handleHierarchyClose = () => {
    setHierarchyMenuOpen(false);
    setClusterMenuOpen(false);
    setHierarchyAnchorEl(null);
  };

  const handleProjectSelect = () => {
    onVariantChange({ ...variant, hierarchy: 'Project' });
    handleHierarchyClose();
  };

  const handleClusterSelect = (clusterName: string) => {
    onVariantChange({ ...variant, hierarchy: `Cluster:${clusterName}` });
    handleHierarchyClose();
  };

  const elementDropdownTitle = React.useMemo(() => {
    switch (templateChannel) {
      case 'HER': return 'HER Modules';
      case 'Web': return 'Web Widgets';
      case 'Email':
      default:
        return 'Email Elements';
    }
  }, [templateChannel]);

  // If only rendering upper section, return just that
  if (renderOnlyUpperSection) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32, // h-8 = 32px
              minWidth: 180, // w-[180px]
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            <Divider />
            <Tooltip title="Coming soon">
              <MenuItem 
                onClick={() => {}} // No-op: functionality preserved but disabled
                onMouseEnter={() => {}} // No-op: prevent cluster menu from opening
                disabled={true}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  fontSize: '0.875rem',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                    flex: 1
                  }}
                >
                  Clusters (Coming soon)
                </Typography>
                <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>
            </Tooltip>
            {clusterMenuOpen && clusters.length > 0 && (
              <Menu
                open={clusterMenuOpen}
                anchorEl={hierarchyAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={() => setClusterMenuOpen(false)}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    mt: 0.5,
                    ml: 0.5,
                    boxShadow: 2
                  }
                }}
                MenuListProps={{
                  sx: {
                    minWidth: 200,
                    py: 0.5,
                    maxHeight: 400,
                    overflow: 'auto',
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                {clusters.map(c => (
                  <MenuItem 
                    key={c} 
                    onClick={() => handleClusterSelect(c)}
                    selected={hierarchyValue === c}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      fontSize: '0.875rem'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        margin: 0,
                        padding: 0,
                        width: '100%'
                      }}
                    >
                      {c}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
          onClick={async () => {
            if (!onSave) return;
            try { setSaving(true); await onSave(); } finally { setSaving(false); }
          }}
          disabled={!templateKey || saving || modifiedCount === 0}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={(theme) => ({
              height: 32,
              fontSize: '0.875rem',
              textTransform: 'none',
              minWidth: 100,
              ...(modifiedCount === 0 && { 
                bgcolor: alpha(theme.palette.primary.main, 0.3), // Bluish background with reduced opacity
                color: alpha(theme.palette.primary.main, 0.5), // Bluish text color
                opacity: 0.6,
                cursor: 'not-allowed',
                filter: 'blur(0.5px)',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.3), // Prevent hover effect
                }
              })
            })}
          >
            {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
          </Button>
        </Box>
      </Box>
    );
  }

  // If only rendering lower section, return just that
  if (renderOnlyLowerSection) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={templateChannel} 
              size="small"
              color="primary"
              sx={{ 
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            />
            {displayPrefix && (
              <Chip
                label={displayPrefix}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 20,
                  px: 1.5, // px-1.5 equivalent
                  py: 0.5, // py-0.5 equivalent
                  borderColor: theme => alpha(theme.palette.primary.main, 0.5), // border-primary/50 equivalent
                  color: 'primary.main', // text-primary equivalent
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1) // bg-primary/10 equivalent
                }}
              />
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {nameWithoutPrefix || templateKey || 'Select a template'}
            </Typography>
          </Box>
          {templateChannel !== 'HER' && onPreviewModeChange && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: 'background.default'
              }}
            >
              <Button
                variant={previewMode === 'desktop' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('desktop')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Monitor size={16} />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('mobile')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Smartphone size={16} />
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={localeDisplayText} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {templateChannel !== 'Web' && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={elementKey}
                  onChange={(e) => onElementChange(e.target.value as string)}
                  disabled={!elements.length}
                  displayEmpty
                  sx={{
                    height: 32,
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        zIndex: 1300, // Ensure menu appears above other elements
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    },
                    MenuListProps: {
                      sx: {
                        maxHeight: 400,
                        overflow: 'auto'
                      }
                    },
                    disableAutoFocus: false,
                    disableEnforceFocus: false,
                    disableRestoreFocus: false
                  }}
                  renderValue={(value) => {
                    const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                      .find(e => e.elementKey === value);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                        <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                      </Box>
                    );
                  }}
                >
                  {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                    <MenuItem 
                      key={e.elementKey} 
                      value={e.elementKey} 
                      sx={{ 
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                        <span>{e.displayName}</span>
                      </Box>
                      {signedOffElements.has(e.elementKey) && (
                        <CheckCheck size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl 
              size="small" 
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={(() => {
                  // Ensure the value matches one of the available scenario values
                  const currentValue = variant.scenario || '';
                  
                  // If no scenarios available, return empty string or current value
                  if (scenarios.length === 0) {
                    return currentValue || '';
                  }
                  
                  // Check if current value exists in available scenarios
                  const hasMatchingValue = scenarios.some(ds => ds.scenarioId === currentValue);
                  
                  if (!hasMatchingValue) {
                    // If current value doesn't match, try to find default scenario first
                    const defaultScenario = scenarios.find(ds => ds.defaultScenario === true);
                    if (defaultScenario) {
                      return defaultScenario.scenarioId;
                    }
                    // Otherwise use the first scenario
                    return scenarios[0]?.scenarioId || '';
                  }
                  
                  return currentValue;
                })()}
                onChange={(e) => {
                  // Store the scenarioId (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The scenarioDisplayName (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  const selectedScenario = scenarios.find(ds => ds.scenarioId === selectedValue);
                  console.log('[EditorToolbar] Scenario selected:', {
                    label: selectedScenario?.scenarioDisplayName, // Displayed in UI
                    scenarioId: selectedValue, // Stored and sent to API (e.g., "MS_1")
                    productType: selectedScenario?.productType, // Verify channel match
                    willSendToAPI: selectedValue,
                    allScenarios: scenarios,
                    currentVariant: variant
                  });
                  if (selectedValue) {
                    onVariantChange({ ...variant, scenario: selectedValue });
                  }
                }}
                displayEmpty
                disabled={scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenarioId (e.g., "MS_1"), but we display the scenarioDisplayName (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(ds => ds.scenarioId === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                          {scenariosError ? 'Error loading' : 'No scenarios'}
                        </span>
                      </Box>
                    );
                  }
                  // Display scenarioDisplayName in UI, but scenarioId (e.g., "MS_1") is stored in variant.scenario and sent to API
                  const displayName = selected?.scenarioDisplayName || selected?.scenarioId || value;
                  return (
                    <Tooltip title={truncateText(displayName, 14) !== displayName ? displayName : ''} placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(displayName, 14)}</span>
                    </Box>
                    </Tooltip>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  [
                    ...scenarios.map((ds, index) => (
                      <MenuItem 
                        key={`${ds.scenarioId}-${index}`}
                        value={ds.scenarioId} 
                        sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                        title={truncateText(ds.scenarioDisplayName || ds.scenarioId, 14) !== (ds.scenarioDisplayName || ds.scenarioId) ? (ds.scenarioDisplayName || ds.scenarioId) : ''}
                      >
                        {(ds.scenarioDisplayName || ds.scenarioId).toLowerCase()}
                      </MenuItem>
                    )),
                    <Divider key="divider" />,
                    <MenuItem 
                      key="manage-scenarios"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = '/content-management/data-scenarios';
                      }}
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                        px: 2,
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <PlusCircle size={16} />
                      <span>Manage Scenarios...</span>
                    </MenuItem>
                  ]
                )}
              </Select>
              {scenariosError && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  {scenariosError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
    );
  }

  // Default: render both sections
  return (
    <>
      {/* Top Toolbar Section - Full Width */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32,
              minWidth: 180,
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            <Divider />
            <Tooltip title="Coming soon">
              <MenuItem 
                onClick={() => {}} // No-op: functionality preserved but disabled
                onMouseEnter={() => {}} // No-op: prevent cluster menu from opening
                disabled={true}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  fontSize: '0.875rem',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                    flex: 1
                  }}
                >
                  Clusters (Coming soon)
                </Typography>
                <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>
            </Tooltip>
            {clusterMenuOpen && clusters.length > 0 && (
              <Menu
                open={clusterMenuOpen}
                anchorEl={hierarchyAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={() => setClusterMenuOpen(false)}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    mt: 0.5,
                    ml: 0.5,
                    boxShadow: 2
                  }
                }}
                MenuListProps={{
                  sx: {
                    minWidth: 200,
                    py: 0.5,
                    maxHeight: 400,
                    overflow: 'auto',
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                {clusters.map(c => (
                  <MenuItem 
                    key={c} 
                    onClick={() => handleClusterSelect(c)}
                    selected={hierarchyValue === c}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      px: 2,
                      py: 1,
                      minHeight: 40,
                      fontSize: '0.875rem'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        margin: 0,
                        padding: 0,
                        width: '100%'
                      }}
                    >
                      {c}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
          onClick={async () => {
            if (!onSave) return;
            try { setSaving(true); await onSave(); } finally { setSaving(false); }
          }}
          disabled={!templateKey || saving || modifiedCount === 0}
            startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={(theme) => ({
              height: 32,
              fontSize: '0.875rem',
              textTransform: 'none',
              minWidth: 100,
              ...(modifiedCount === 0 && { 
                bgcolor: alpha(theme.palette.primary.main, 0.3), // Bluish background with reduced opacity
                color: alpha(theme.palette.primary.main, 0.5), // Bluish text color
                opacity: 0.6,
                cursor: 'not-allowed',
                filter: 'blur(0.5px)',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.3), // Prevent hover effect
                }
              })
            })}
          >
            {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
          </Button>
        </Box>
      </Box>

      {/* Preview Header Section - Lower Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={templateChannel} 
              size="small"
              color="primary"
              sx={{ 
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            />
            {displayPrefix && (
              <Chip
                label={displayPrefix}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 20,
                  px: 1.5, // px-1.5 equivalent
                  py: 0.5, // py-0.5 equivalent
                  borderColor: theme => alpha(theme.palette.primary.main, 0.5), // border-primary/50 equivalent
                  color: 'primary.main', // text-primary equivalent
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1) // bg-primary/10 equivalent
                }}
              />
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {nameWithoutPrefix || templateKey || 'Select a template'}
            </Typography>
          </Box>
          {templateChannel !== 'HER' && onPreviewModeChange && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: 'background.default'
              }}
            >
              <Button
                variant={previewMode === 'desktop' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('desktop')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Monitor size={16} />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('mobile')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Smartphone size={16} />
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={localeDisplayText} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {templateChannel !== 'Web' && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={elementKey}
                  onChange={(e) => onElementChange(e.target.value as string)}
                  disabled={!elements.length}
                  displayEmpty
                  sx={{
                    height: 32,
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        zIndex: 1300, // Ensure menu appears above other elements
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    },
                    MenuListProps: {
                      sx: {
                        maxHeight: 400,
                        overflow: 'auto'
                      }
                    },
                    disableAutoFocus: false,
                    disableEnforceFocus: false,
                    disableRestoreFocus: false
                  }}
                  renderValue={(value) => {
                    const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                      .find(e => e.elementKey === value);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                        <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                      </Box>
                    );
                  }}
                >
                  {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                    <MenuItem 
                      key={e.elementKey} 
                      value={e.elementKey} 
                      sx={{ 
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                        <span>{e.displayName}</span>
                      </Box>
                      {signedOffElements.has(e.elementKey) && (
                        <CheckCheck size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl 
              size="small" 
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={(() => {
                  // Ensure the value matches one of the available scenario values
                  const currentValue = variant.scenario || '';
                  
                  // If no scenarios available, return empty string or current value
                  if (scenarios.length === 0) {
                    return currentValue || '';
                  }
                  
                  // Check if current value exists in available scenarios
                  const hasMatchingValue = scenarios.some(ds => ds.scenarioId === currentValue);
                  
                  if (!hasMatchingValue) {
                    // If current value doesn't match, try to find default scenario first
                    const defaultScenario = scenarios.find(ds => ds.defaultScenario === true);
                    if (defaultScenario) {
                      return defaultScenario.scenarioId;
                    }
                    // Otherwise use the first scenario
                    return scenarios[0]?.scenarioId || '';
                  }
                  
                  return currentValue;
                })()}
                onChange={(e) => {
                  // Store the scenarioId (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The scenarioDisplayName (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  const selectedScenario = scenarios.find(ds => ds.scenarioId === selectedValue);
                  console.log('[EditorToolbar] Scenario selected:', {
                    label: selectedScenario?.scenarioDisplayName, // Displayed in UI
                    scenarioId: selectedValue, // Stored and sent to API (e.g., "MS_1")
                    productType: selectedScenario?.productType, // Verify channel match
                    willSendToAPI: selectedValue,
                    allScenarios: scenarios,
                    currentVariant: variant
                  });
                  if (selectedValue) {
                    onVariantChange({ ...variant, scenario: selectedValue });
                  }
                }}
                displayEmpty
                disabled={scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenarioId (e.g., "MS_1"), but we display the scenarioDisplayName (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(ds => ds.scenarioId === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                          {scenariosError ? 'Error loading' : 'No scenarios'}
                        </span>
                      </Box>
                    );
                  }
                  // Display scenarioDisplayName in UI, but scenarioId (e.g., "MS_1") is stored in variant.scenario and sent to API
                  const displayName = selected?.scenarioDisplayName || selected?.scenarioId || value;
                  return (
                    <Tooltip title={truncateText(displayName, 14) !== displayName ? displayName : ''} placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(displayName, 14)}</span>
                    </Box>
                    </Tooltip>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  [
                    ...scenarios.map((ds, index) => (
                      <MenuItem 
                        key={`${ds.scenarioId}-${index}`}
                        value={ds.scenarioId} 
                        sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                        title={truncateText(ds.scenarioDisplayName || ds.scenarioId, 14) !== (ds.scenarioDisplayName || ds.scenarioId) ? (ds.scenarioDisplayName || ds.scenarioId) : ''}
                      >
                        {(ds.scenarioDisplayName || ds.scenarioId).toLowerCase()}
                      </MenuItem>
                    )),
                    <Divider key="divider" />,
                    <MenuItem 
                      key="manage-scenarios"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = '/content-management/data-scenarios';
                      }}
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                        px: 2,
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <PlusCircle size={16} />
                      <span>Manage Scenarios...</span>
                    </MenuItem>
                  ]
                )}
              </Select>
              {scenariosError && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  {scenariosError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
    </>
  );
}


